import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import {
  PawPrint,
  Camera,
  Plus,
  BarChart3,
  Search,
  Dog,
  BookOpen,
  Activity,
  Key,
  Settings,
  Shield,
  FileText,
  Stethoscope,
  TrendingUp,
  Network,
  AlertTriangle,
  MapPin,
  DollarSign,
} from "lucide-react";

const Sidebar = ({ userRole, mobileMenuOpen, setMobileMenuOpen }) => {
  const { themeClasses, effectiveTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      id: "overview",
      name: "Overview",
      icon: BarChart3,
      path: "/dashboard",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "identify",
      name: "Identify Pet",
      icon: Camera,
      path: "/dashboard/identify",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "dogbreed",
      name: "Dog Breed AI",
      icon: Dog,
      path: "/dashboard/dog-breed",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "breedinfo",
      name: "Breed Information",
      icon: BookOpen,
      path: "/dashboard/breed-info",
      roles: ["pro", "admin"],
    },
    {
      id: "breedmanager",
      name: "Breed Database",
      icon: Search,
      path: "/dashboard/breed-database",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "mypets",
      name: "My Pets",
      icon: PawPrint,
      path: "/dashboard/pets",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "register",
      name: "Register Pet",
      icon: Plus,
      path: "/dashboard/register",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "lostdogs",
      name: "Lost Dogs",
      icon: AlertTriangle,
      path: "/dashboard/lost-dogs",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "reportlostdog",
      name: "Report Lost Dog",
      icon: MapPin,
      path: "/dashboard/register-lost-dog",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "foundpet",
      name: "Found Pet",
      icon: Search,
      path: "/dashboard/found-pet",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "vetprices",
      name: "Vet Price Comparison",
      icon: DollarSign,
      path: "/dashboard/vet-prices",
      roles: ["free", "pro", "admin"],
    },
    {
      id: "health",
      name: "Health Records",
      icon: Activity,
      path: "/dashboard/health",
      roles: ["pro", "admin"],
    },
    {
      id: "vaccinations",
      name: "Vaccination Records",
      icon: Shield,
      path: "/dashboard/vaccinations",
      roles: ["pro", "admin"],
    },
    {
      id: "apikeys",
      name: "API Keys",
      icon: Key,
      path: "/dashboard/api-keys",
      roles: ["pro", "admin"],
    },
    {
      id: "apidocs",
      name: "API Documentation",
      icon: FileText,
      path: "/dashboard/api/detailed",
      roles: ["pro", "admin"],
    },
    {
      id: "analytics",
      name: "Admin Analytics",
      icon: BarChart3,
      path: "/dashboard/analytics",
      roles: ["admin"],
    },
    {
      id: "businessplan",
      name: "Business Plan",
      icon: TrendingUp,
      path: "/dashboard/business-plan",
      roles: ["admin"],
    },
    {
      id: "vetdashboard",
      name: "Veterinarian Dashboard",
      icon: Stethoscope,
      path: "/dashboard/vet-dashboard",
      roles: ["admin"],
    },
    {
      id: "systemarchitecture",
      name: "System Architecture",
      icon: Network,
      path: "/dashboard/system-architecture",
      roles: ["admin"],
    },
    {
      id: "settings",
      name: "Settings",
      icon: Settings,
      path: "/dashboard/settings",
      roles: ["free", "pro", "admin"],
    },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles.includes(userRole)
  );

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false); // Close mobile menu on navigation
  };

  const isActive = (path) => {
    if (path === "/dashboard") {
      return (
        location.pathname === "/dashboard" ||
        location.pathname === "/dashboard/"
      );
    }
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 ${
          effectiveTheme === "light"
            ? "bg-gray-50 border-gray-200"
            : "bg-gray-800/50 backdrop-blur-sm border-cyan-500/20"
        } border-r transform transition-transform duration-300 ease-in-out z-40 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="p-4 h-full overflow-y-auto">
          <nav className="space-y-2">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                    active
                      ? `${themeClasses.bg.button} text-white border ${themeClasses.border.accent}`
                      : `${themeClasses.text.secondary} hover:${themeClasses.text.primary} ${themeClasses.bg.cardHover} border border-transparent`
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? "text-white" : ""}`} />
                  <span className="font-medium">{item.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
